/**
 * Reference label matching (ADR 0005) — longest label first, case-insensitive.
 */

/**
 * @typedef {Object} EntityReference
 * @property {string} entityType
 * @property {string|null} entityId
 * @property {string} label
 * @property {boolean} [canView]
 */

/**
 * @typedef {string | { refIndex: number, matchedText: string }} TextSegment
 */

/**
 * Split text by reference labels (longest first).
 * @param {string} input
 * @param {EntityReference[]} references
 * @returns {TextSegment[]}
 */
export function splitTextByReferences(input, references) {
  if (!input || !references?.length) return [input || ''];

  const indexed = references
    .map((ref, i) => ({ refIndex: i, label: ref.label || '', inline: ref.inline }))
    .filter((r) => r.label.length > 0 && r.inline !== false)
    .sort((a, b) => b.label.length - a.label.length);

  let segments = [input];

  for (const { refIndex, label } of indexed) {
    const next = [];
    for (const seg of segments) {
      if (typeof seg !== 'string') {
        next.push(seg);
        continue;
      }
      next.push(...splitOneLabel(seg, label, refIndex));
    }
    segments = next;
  }

  return segments.filter((s) => typeof s !== 'string' || s.length > 0);
}

function splitOneLabel(input, label, refIndex) {
  const haystack = input.toLowerCase();
  const needle = label.toLowerCase();
  const out = [];
  let cursor = 0;

  while (cursor < input.length) {
    const idx = haystack.indexOf(needle, cursor);
    if (idx === -1) {
      out.push(input.slice(cursor));
      break;
    }
    if (idx > cursor) out.push(input.slice(cursor, idx));
    out.push({ refIndex, matchedText: input.slice(idx, idx + label.length) });
    cursor = idx + label.length;
  }

  return out;
}

/**
 * References whose labels never appear in any text part.
 * @param {EntityReference[]} references
 * @param {Array} parts
 * @param {string} [fallbackContent]
 * @returns {EntityReference[]}
 */
export function getUnmatchedReferences(references, parts, fallbackContent) {
  if (!references?.length) return [];

  const haystack = (
    parts?.length
      ? parts
          .filter((p) => p.kind === 'text')
          .map((p) => p.content)
          .join('\n')
      : fallbackContent || ''
  ).toLowerCase();

  return references.filter((r) => {
    if (!r.label) return false;
    // List-only references always appear in the panel even when the label is in prose.
    if (r.inline === false) return true;
    return !haystack.includes(r.label.toLowerCase());
  });
}
